import React, { useMemo, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';

const customStyles = {
  container: (provided) => ({
    ...provided,
    width: '100%',
    margin: '0',
    cursor: 'text',
  }),
  menu: (provided) => ({
    ...provided,
    width: '100%',
  }),
  control: (provided) => ({
    ...provided,
    minHeight: '37px',
    maxHeight: '37px',
    height: '37px',
    cursor: 'text',
  }),
  valueContainer: (provided) => ({
    ...provided,
    maxHeight: '37px',
    overflow: 'hidden',
    display: 'flex',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    padding: '2px 16px 2px 12px',
    gap: '6px',
    position: 'relative',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '35px',
    paddingLeft: '6px',
  }),
  placeholder: (provided) => ({
    ...provided,
    whiteSpace: 'nowrap',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    flexShrink: 0,
    margin: '2px 6px 2px 0',
    paddingRight: '6px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    fontSize: '14px',
    padding: '2px 6px',
    whiteSpace: 'nowrap',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    borderRadius: '0 4px 4px 0',
    padding: '0 4px',
    '&:hover': {
      backgroundColor: '#e5e7eb',
      color: '#ef4444',
    },
  }),
};

const LandingNamesSelect = ({ landings, selectedLandingNames, onChange }) => {
  const [customOptions, setCustomOptions] = useState([]);
  const selectRef = useRef(null);

  const baseOptions = useMemo(
    () =>
      landings.map((landing) => ({
        value: landing.displayName,
        label: landing.displayName,
      })),
    [landings]
  );

  const options = useMemo(() => {
    const optionMap = new Map();
    [...baseOptions, ...customOptions].forEach((option) => {
      if (!optionMap.has(option.value.toLowerCase())) {
        optionMap.set(option.value.toLowerCase(), option);
      }
    });
    return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [baseOptions, customOptions]);

  const value = useMemo(
    () => selectedLandingNames.map((name) => ({ value: name, label: name })),
    [selectedLandingNames]
  );

  const handleChange = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      onChange([]);
      return;
    }

    onChange(selectedOptions.map((option) => option.value));
  };

  const handleCreateOption = (inputValue) => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) {
      return;
    }

    setCustomOptions((prev) => {
      if (prev.some((option) => option.value.toLowerCase() === trimmedValue.toLowerCase())) {
        return prev;
      }

      return [...prev, { value: trimmedValue, label: trimmedValue }];
    });

    const normalized = trimmedValue.replace(/\s+/g, ' ');
    if (!selectedLandingNames.some((name) => name.toLowerCase() === normalized.toLowerCase())) {
      onChange([...selectedLandingNames, normalized]);
    }
  };

  return (
    <div className="landing-select-wrapper" style={{ position: 'relative', width: '100%' }}>
      <CreatableSelect
        ref={selectRef}
        isMulti
        value={value}
        onChange={handleChange}
        onCreateOption={handleCreateOption}
        options={options}
        styles={customStyles}
        placeholder="Select landings..."
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        menuPlacement="auto"
      />
      <button
        type="button"
        className="landing-select-overlay"
        onClick={() => selectRef.current?.focus()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '40px',
          background: 'transparent',
          border: 'none',
          cursor: 'text',
        }}
        aria-label="Add landing"
      />
    </div>
  );
};

export default LandingNamesSelect;


